import { Accordion, AccordionHeader, Card } from '@ps/sports-ui-component-library';
import { FeatureFlag } from '@ps/web-core-enums';
import type { MyBet } from '@ps/web-sports-api-types';
import { PriceType } from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';

import styles from './Bet.scss';
import SgmBet from './SgmBet';
import AddSelections from './components/AddSelections/AddSelections';
import BetDetails from './components/BetDetails/BetDetails';
import CashOut from './components/CashOut/CashOut';
import CashOutBanner from './components/CashOutBanner/CashOutBanner';
import Header from './components/Header/Header';
import Leg from './components/Leg/Leg';
import StakeAndReturn from './components/StakeAndReturn/StakeAndReturn';
import WinBanner from './components/WinBanner/WinBanner';

export interface Props {
  bet: MyBet;
  cashedOutAmount?: number;
  open: boolean;
}

const Bet: React.FC<Props> = ({ bet, cashedOutAmount, open }) => {
  const { stake = 0, stakePerLine = 0, actualWin, isSettled } = bet;
  const isProfitableBet = actualWin ? actualWin >= stake : false;
  const isCashedOut = bet.result === 'CASHED_OUT';
  const hasWon = bet.result === 'WON' || bet.result === 'PLACED';
  const hasWinBanner =
    (hasWon || isProfitableBet) &&
    !!actualWin &&
    bet.result !== 'CASHED_OUT' &&
    bet.result !== 'VOID';

  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const isCashOutEnabled = useFeature(FeatureFlag.ISPCashOut);
  const isAddSelectionsEnabled = useFeature(FeatureFlag.ISPEnableAddToBetSlipMyBets);
  const isStartingPriceBet = bet.legs.some((leg) =>
    leg.parts.some((part) => part.priceType === PriceType.STARTING)
  );

  return (
    <Card>
      <Accordion
        isInitiallyExpanded={open}
        renderTitle={(props) => (
          <AccordionHeader
            {...props}
            title={
              <Header
                betType={bet.betType}
                odds={bet.odds}
                result={bet.result}
                stakeAmount={stakePerLine}
                isEachWay={bet.legBetType === 'Eachway'}
                numLines={bet.numLines}
                currency={bet.currency}
                isSgm={bet.isSgm}
              />
            }
          />
        )}
      >
        {hasWinBanner && <WinBanner actualWin={actualWin} currency={bet.currency} />}

        {isCashedOut && !!actualWin && (
          <CashOutBanner cashedOutAmount={actualWin} currency={bet.currency} />
        )}

        {!isSettled && !!cashedOutAmount && (
          <CashOutBanner cashedOutAmount={cashedOutAmount} currency={bet.currency} />
        )}

        <div className={classNames(styles.content, { [styles.sgmContent]: bet.isSgm })}>
          {bet.isSgm ? (
            <SgmBet bet={bet} />
          ) : (
            <div className={classNames(styles.legs)}>
              {bet.legs.map((leg) => {
                const [part] = leg.parts;

                return (
                  <Leg
                    key={`${part.marketId}-${part.selectionId}`}
                    {...leg}
                    isSettled={isSettled}
                    currency={bet.currency}
                  />
                );
              })}
            </div>
          )}
          <StakeAndReturn
            result={bet.result}
            actualWin={actualWin}
            totalStake={stake}
            potentialWin={bet.potentialWin}
            isSettled={isSettled}
            cashedOutAmount={cashedOutAmount}
            currency={bet.currency}
            walletInfo={bet.walletInfo}
            isProfitableBet={isProfitableBet}
            isStartingPriceBet={isStartingPriceBet}
          />
          {isCashOutEnabled && !isSettled && !cashedOutAmount && !isSelfExcluded && (
            <CashOut bet={bet} currency={bet.currency} />
          )}
          <BetDetails bet={bet} cashedOutAmount={cashedOutAmount} />
          {isAddSelectionsEnabled && <AddSelections bet={bet} />}
        </div>
      </Accordion>
    </Card>
  );
};

export default memo(Bet);
